import { assertSha256, base64ToBytes, bytesToBase64, sha256 } from "./hash.js";
import type { LocalProjectStore } from "./project-store.js";
import {
  DEFAULT_STORAGE_LIMITS,
  StorageConflictError,
  StorageIntegrityError,
  StorageValidationError,
  type JsonObject,
  type ProjectSnapshot,
  type StorageLimits,
} from "./types.js";
import { assertId, validateJsonDocument, validateJsonTree, validateMetadata } from "./validation.js";

export const PORTABLE_BUNDLE_FORMAT = "tattoo-stencil-project";
export const PORTABLE_BUNDLE_VERSION = 1;

export interface BundleLimits {
  maxEncodedBytes: number;
  maxExpandedBytes: number;
  maxAssetBytes: number;
  maxAssets: number;
}

export const DEFAULT_BUNDLE_LIMITS: Readonly<BundleLimits> = Object.freeze({
  maxEncodedBytes: 96 * 1024 * 1024,
  maxExpandedBytes: 64 * 1024 * 1024,
  maxAssetBytes: 25 * 1024 * 1024,
  maxAssets: 256,
});

interface PortableAssetEntry {
  id: string;
  path: string;
  mediaType: string;
  byteLength: number;
  sha256: string;
}

interface PortableBundleV1 {
  manifest: {
    format: typeof PORTABLE_BUNDLE_FORMAT;
    version: typeof PORTABLE_BUNDLE_VERSION;
    exportedAt: string;
    documentPath: "document.json";
    project: {
      id: string;
      title: string;
      tags: string[];
      notes: string;
      revision: number;
      createdAt: string;
      updatedAt: string;
    };
    assets: PortableAssetEntry[];
    provenance: { generator: string; formatVersion: 1 };
  };
  files: Record<string, JsonObject | string>;
}

export interface ParsedPortableBundle {
  project: PortableBundleV1["manifest"]["project"];
  document: JsonObject;
  assets: Array<{ id: string; mediaType: string; bytes: Uint8Array; sha256: string }>;
}

export async function exportPortableBundle(
  snapshot: ProjectSnapshot,
  options: { now?: Date; limits?: BundleLimits; generator?: string } = {},
): Promise<string> {
  const limits = options.limits ?? DEFAULT_BUNDLE_LIMITS;
  const assetsById = new Map(snapshot.assets.map((asset) => [asset.id, asset]));
  const requiredAssetIds = [...new Set(snapshot.checkpoint.assetIds)];
  if (requiredAssetIds.length > limits.maxAssets) throw new StorageValidationError("Bundle contains too many assets");
  const files: Record<string, JsonObject | string> = { "document.json": structuredClone(snapshot.checkpoint.document) };
  const assets: PortableAssetEntry[] = [];
  let expandedBytes = new TextEncoder().encode(JSON.stringify(snapshot.checkpoint.document)).byteLength;
  for (const assetId of requiredAssetIds) {
    const asset = assetsById.get(assetId);
    if (!asset) throw new StorageIntegrityError(`Bundle snapshot is missing required asset: ${assetId}`);
    if (asset.byteLength > limits.maxAssetBytes) throw new StorageValidationError(`Asset ${asset.id} exceeds the bundle limit`);
    const path = `assets/${asset.id}.bin`;
    expandedBytes += asset.byteLength;
    if (expandedBytes > limits.maxExpandedBytes) throw new StorageValidationError("Bundle expansion exceeds its limit");
    files[path] = bytesToBase64(asset.bytes);
    assets.push({
      id: asset.id,
      path,
      mediaType: asset.mediaType,
      byteLength: asset.byteLength,
      sha256: asset.sha256,
    });
  }
  const bundle: PortableBundleV1 = {
    manifest: {
      format: PORTABLE_BUNDLE_FORMAT,
      version: PORTABLE_BUNDLE_VERSION,
      exportedAt: (options.now ?? new Date()).toISOString(),
      documentPath: "document.json",
      project: {
        id: snapshot.project.id,
        title: snapshot.project.title,
        tags: [...snapshot.project.tags],
        notes: snapshot.project.notes,
        revision: snapshot.checkpoint.revision,
        createdAt: snapshot.project.createdAt,
        updatedAt: snapshot.project.updatedAt,
      },
      assets,
      provenance: { generator: options.generator ?? "tattoo-stencil-local", formatVersion: 1 },
    },
    files,
  };
  const json = JSON.stringify(bundle);
  if (new TextEncoder().encode(json).byteLength > limits.maxEncodedBytes) {
    throw new StorageValidationError("Encoded bundle exceeds its limit");
  }
  return json;
}

export async function parsePortableBundle(
  source: string | Uint8Array,
  options: { bundleLimits?: BundleLimits; storageLimits?: StorageLimits } = {},
): Promise<ParsedPortableBundle> {
  const bundleLimits = options.bundleLimits ?? DEFAULT_BUNDLE_LIMITS;
  const storageLimits = options.storageLimits ?? DEFAULT_STORAGE_LIMITS;
  const sourceBytes = typeof source === "string" ? new TextEncoder().encode(source) : source;
  if (sourceBytes.byteLength > bundleLimits.maxEncodedBytes) throw new StorageValidationError("Encoded bundle exceeds its limit");
  let decoded: unknown;
  try {
    decoded = JSON.parse(typeof source === "string" ? source : new TextDecoder("utf-8", { fatal: true }).decode(source));
  } catch {
    throw new StorageValidationError("Bundle is not valid UTF-8 JSON");
  }
  validateJsonTree(decoded, { maxDepth: 70, maxNodes: 110_000, maxStringLength: bundleLimits.maxEncodedBytes });
  const bundle = decoded as unknown as PortableBundleV1;
  if (bundle.manifest?.format !== PORTABLE_BUNDLE_FORMAT || bundle.manifest.version !== PORTABLE_BUNDLE_VERSION) {
    throw new StorageValidationError("Unsupported project bundle format or version");
  }
  if (bundle.manifest.documentPath !== "document.json") throw new StorageValidationError("Invalid document path");
  assertSafePath(bundle.manifest.documentPath);
  assertId(bundle.manifest.project?.id, "project.id");
  const metadata = validateMetadata(bundle.manifest.project, storageLimits);
  if (!Number.isSafeInteger(bundle.manifest.project.revision) || bundle.manifest.project.revision < 1) {
    throw new StorageValidationError("Bundle revision is invalid");
  }
  if (!Array.isArray(bundle.manifest.assets) || bundle.manifest.assets.length > bundleLimits.maxAssets) {
    throw new StorageValidationError("Bundle contains too many assets");
  }
  if (!bundle.files || typeof bundle.files !== "object" || Array.isArray(bundle.files)) {
    throw new StorageValidationError("Bundle files are invalid");
  }
  const allowedPaths = new Set<string>([bundle.manifest.documentPath]);
  const document = bundle.files[bundle.manifest.documentPath];
  if (!document || typeof document !== "object" || Array.isArray(document)) {
    throw new StorageValidationError("Bundle document is missing or invalid");
  }
  validateJsonDocument(document, storageLimits);
  const assets: ParsedPortableBundle["assets"] = [];
  let expandedBytes = new TextEncoder().encode(JSON.stringify(document)).byteLength;
  const assetIds = new Set<string>();
  for (const entry of bundle.manifest.assets) {
    validateAssetEntry(entry, bundleLimits);
    if (assetIds.has(entry.id)) throw new StorageValidationError(`Duplicate asset ID: ${entry.id}`);
    assetIds.add(entry.id);
    allowedPaths.add(entry.path);
    const encoded = bundle.files[entry.path];
    if (typeof encoded !== "string") throw new StorageValidationError(`Missing asset file: ${entry.path}`);
    const bytes = base64ToBytes(encoded, Math.min(entry.byteLength, bundleLimits.maxAssetBytes));
    if (bytes.byteLength !== entry.byteLength) throw new StorageIntegrityError(`Asset ${entry.id} has the wrong byte length`);
    expandedBytes += bytes.byteLength;
    if (expandedBytes > bundleLimits.maxExpandedBytes) throw new StorageValidationError("Bundle expansion exceeds its limit");
    if ((await sha256(bytes)) !== entry.sha256) throw new StorageIntegrityError(`Asset ${entry.id} has a hash mismatch`);
    assets.push({ id: entry.id, mediaType: entry.mediaType, bytes, sha256: entry.sha256 });
  }
  for (const path of Object.keys(bundle.files)) {
    assertSafePath(path);
    if (!allowedPaths.has(path)) throw new StorageValidationError(`Unlisted bundle file: ${path}`);
  }
  return {
    project: {
      ...bundle.manifest.project,
      title: metadata.title,
      tags: [...metadata.tags],
      notes: metadata.notes,
    },
    document: structuredClone(document),
    assets,
  };
}

export async function restorePortableBundle(
  store: LocalProjectStore,
  source: string | Uint8Array,
  options: {
    idFactory?: () => string;
    bundleLimits?: BundleLimits;
    storageLimits?: StorageLimits;
    now?: Date;
  } = {},
): Promise<ProjectSnapshot> {
  const parsed = await parsePortableBundle(source, options);
  let projectId = parsed.project.id;
  if (await store.hasProject(projectId)) {
    projectId = options.idFactory?.() ?? globalThis.crypto.randomUUID();
    assertId(projectId, "copied project ID");
    if (await store.hasProject(projectId)) throw new StorageConflictError("Generated project copy ID already exists");
  }
  return store.saveCheckpoint({
    projectId,
    createOnly: true,
    revision: parsed.project.revision,
    metadata: { title: parsed.project.title, tags: parsed.project.tags, notes: parsed.project.notes },
    document: parsed.document,
    assetIds: parsed.assets.map((asset) => asset.id),
    assets: parsed.assets,
    ...(options.now === undefined ? {} : { now: options.now }),
  });
}

function validateAssetEntry(entry: PortableAssetEntry, limits: BundleLimits): void {
  if (!entry || typeof entry !== "object") throw new StorageValidationError("Bundle asset entry is invalid");
  assertId(entry.id, "asset.id");
  assertSafePath(entry.path);
  if (entry.path !== `assets/${entry.id}.bin`) throw new StorageValidationError("Asset path does not match its ID");
  if (typeof entry.mediaType !== "string" || entry.mediaType.length < 3 || entry.mediaType.length > 127) {
    throw new StorageValidationError("Asset media type is invalid");
  }
  if (!Number.isSafeInteger(entry.byteLength) || entry.byteLength < 0 || entry.byteLength > limits.maxAssetBytes) {
    throw new StorageValidationError("Asset byte length is invalid");
  }
  assertSha256(entry.sha256, "asset.sha256");
}

function assertSafePath(path: string): void {
  if (
    typeof path !== "string" ||
    path.startsWith("/") ||
    path.includes("\\") ||
    path.split("/").some((part) => part === "" || part === "." || part === "..") ||
    !/^(document\.json|assets\/[A-Za-z0-9][A-Za-z0-9._:-]{0,127}\.bin)$/.test(path)
  ) {
    throw new StorageValidationError("Bundle contains an unsafe path");
  }
}
